import { useState } from 'react'
import type { KeyboardEvent } from 'react'
import { hasWebCrypto } from '@tn/shared'

/** The GCC downloads that can be password-protected (WI 2506). */
export type GccProtectableExport = 'zip' | 'report' | 'html' | 'simple'

const TITLES: Record<GccProtectableExport, string> = {
  zip: 'Download ZIP',
  report: 'Download Combined Report',
  html: 'Download GCC Dashboard',
  // The client-facing dashboard without the internal detail tabs (WI 2822)
  simple: 'Download GCC Dashboard (Simple)',
}

const MIN_LENGTH = 4
// Same cap as PasswordSchema in @tn/shared
const MAX_LENGTH = 200

/** Why a typed password can't be used, or null when it can. A blank password means no protection. */
function passwordProblem(password: string, confirm: string): string | null {
  if (!password && !confirm) return null
  if (password.length < MIN_LENGTH) return `Password must be at least ${MIN_LENGTH} characters.`
  if (password.length > MAX_LENGTH) return `Password must be at most ${MAX_LENGTH} characters.`
  if (password !== confirm) return 'Passwords do not match.'
  if (!hasWebCrypto()) return 'Password protection needs a secure (https) connection in this browser.'
  return null
}

/**
 * Optional password for a GCC download. Leaving both fields blank downloads today's
 * unprotected file. The password only lives in this dialog's state; it is never stored.
 */
export default function GccExportPasswordDialog({ kind, onCancel, onConfirm }: {
  kind: GccProtectableExport
  onCancel: () => void
  /** Called with the password, or null to download without one. */
  onConfirm: (password: string | null) => void
}) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    const problem = passwordProblem(password, confirm)
    if (problem) {
      setError(problem)
      return
    }
    onConfirm(password || null)
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') submit()
    else if (e.key === 'Escape') onCancel()
  }

  return (
    <div className="gx-modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="gcc-pw-title">
      {/* Rendered inside the GCC toolbar, which sets white text: the gx-modal rules set their own colours (WI 2526) */}
      <div className="gx-modal gx-modal-sm">
        <div className="gx-modal-head">
          <div>
            <p className="gx-modal-eyebrow">Export · Optional password</p>
            <h2 id="gcc-pw-title" className="gx-modal-title">{TITLES[kind]}</h2>
          </div>
        </div>
        <div className="gx-modal-body">
          <div>
            <p className="gx-modal-text">
              Add a password and recipients must enter it before the report opens. Leave it blank to download without one.
            </p>
            <p className="gx-cap mt-1.5">
              The password hides the report until it is entered; it does not encrypt the file.
            </p>
          </div>
          {kind === 'zip' && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              With a password, the task register CSV and JSON are left out of the ZIP. Download them separately if you need them.
            </p>
          )}
          <div>
            <label htmlFor="gcc-pw" className="gx-field-label">Password (optional)</label>
            <input
              id="gcc-pw"
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(null) }}
              onKeyDown={onKeyDown}
              className="gx-input"
              placeholder="Enter a password"
              autoComplete="new-password"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="gcc-pw-confirm" className="gx-field-label">Confirm Password</label>
            <input
              id="gcc-pw-confirm"
              type="password"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setError(null) }}
              onKeyDown={onKeyDown}
              className="gx-input"
              placeholder="Confirm password"
              autoComplete="new-password"
            />
          </div>
          {error && <p role="alert" className="gx-error">{error}</p>}
        </div>
        <div className="gx-modal-foot">
          <button onClick={onCancel} className="gx-btn">Cancel</button>
          <button onClick={submit} className="gx-btn-primary">
            {password ? 'Download protected' : 'Download'}
          </button>
        </div>
      </div>
    </div>
  )
}
